import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaLinkedin, FaTwitter } from 'react-icons/fa';

const ContactContainer = styled.div``;

const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  background-color: var(--primary-color);
  color: white;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div``;

const InfoTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.75rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-color);
  }
`;

const InfoText = styled.p`
  color: var(--text-light);
  margin-bottom: 2rem;
  line-height: 1.8;
`;

const ContactMethods = styled.div`
  margin-bottom: 2.5rem;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  background-color: rgba(0, 85, 164, 0.1);
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  font-size: 1.25rem;
  margin-right: 1rem;
`;

const ContactDetails = styled.div`
  h4 {
    font-size: 1.125rem;
    margin-bottom: 0.25rem;
  }
  
  p {
    color: var(--text-light);
  }
  
  a {
    color: var(--text-light);
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--primary-color);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0, 85, 164, 0.1);
  color: var(--primary-color);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-3px);
  }
`;

const ContactForm = styled(motion.form)`
  background-color: white;
  padding: 3rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FormTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const InputLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-family: inherit;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-family: inherit;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-family: inherit;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  padding: 0.875rem;
`;

const SuccessMessage = styled(motion.div)`
  background-color: var(--success-color);
  color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form data:', formState);
    setSubmitted(true);
    
    // Reset form after submission
    setTimeout(() => {
      setFormState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setSubmitted(false);
    }, 5000);
  };

  return (
    <ContactContainer>
      <HeroSection>
        <div className="container">
          <HeroTitle>Contact US Energy Trading Compliance Experts</HeroTitle>
          <HeroSubtitle>
            Let's discuss how we can help optimize your US energy trading compliance operations with position limits monitoring and CFTC compliance solutions
          </HeroSubtitle>
        </div>
      </HeroSection>

      <Section>
        <ContactGrid>
          <ContactInfo>
            <InfoTitle>Get In Touch with US Energy Trading Experts</InfoTitle>
            <InfoText>
              Have questions about our US energy trading compliance solutions or want to schedule a position limits monitoring demo? 
              Our team is ready to help you find the right US energy trading compliance tools for your gas and energy trading needs.
            </InfoText>
            
            <ContactMethods>
              <ContactMethod>
                <IconBox>
                  <FaMapMarkerAlt />
                </IconBox>
                <ContactDetails>
                  <h4>Visit Us</h4>
                  <p>Houston, Texas, USA</p>
                </ContactDetails>
              </ContactMethod>
              
              <ContactMethod>
                <IconBox>
                  <FaPhone />
                </IconBox>
                <ContactDetails>
                  <h4>Call Us</h4>
                  <p><a href="tel:+18325557890">+1 (832) 555-7890</a></p>
                </ContactDetails>
              </ContactMethod>
              
              <ContactMethod>
                <IconBox>
                  <FaEnvelope />
                </IconBox>
                <ContactDetails>
                  <h4>Email Us</h4>
                  <p><a href="mailto:info@talgo.com">info@talgo.com</a></p>
                </ContactDetails>
              </ContactMethod>
            </ContactMethods>
            
            <div>
              <h4 style={{ marginBottom: '1rem' }}>Follow Us</h4>
              <SocialLinks>
                <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin />
                </SocialLink>
                <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                  <FaTwitter />
                </SocialLink>
              </SocialLinks>
            </div>
          </ContactInfo>
          
          <ContactForm 
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <FormTitle>Send a Message</FormTitle>
            
            {submitted && (
              <SuccessMessage
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                Thanks for your message! We'll get back to you shortly.
              </SuccessMessage>
            )}
            
            <FormRow>
              <FormGroup>
                <InputLabel htmlFor="name">Your Name</InputLabel>
                <Input 
                  type="text" 
                  id="name"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <InputLabel htmlFor="email">Email Address</InputLabel>
                <Input 
                  type="email" 
                  id="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <InputLabel htmlFor="phone">Phone Number</InputLabel>
                <Input 
                  type="tel" 
                  id="phone"
                  name="phone"
                  value={formState.phone}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup>
                <InputLabel htmlFor="subject">Subject</InputLabel>
                <Select 
                  id="subject"
                  name="subject"
                  value={formState.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="US Energy Trading Demo Request">US Energy Trading Demo Request</option>
                  <option value="Position Limits Monitoring Inquiry">Position Limits Monitoring Inquiry</option>
                  <option value="CFTC Compliance Support">CFTC Compliance Support</option>
                  <option value="US Energy Trading Partnership">US Energy Trading Partnership</option>
                  <option value="Gas Trading Compliance Inquiry">Gas Trading Compliance Inquiry</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <InputLabel htmlFor="message">Your Message</InputLabel>
              <TextArea 
                id="message"
                name="message"
                value={formState.message}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <SubmitButton type="submit" variant="primary">
              Send Message
            </SubmitButton>
          </ContactForm>
        </ContactGrid>
      </Section>
      
      <Section offWhite small>
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d221957.0876151839!2d-95.76941979041502!3d29.817478335274232!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX%2C%20USA!5e0!3m2!1sen!2sus!4v1662000000000!5m2!1sen!2sus" 
          width="100%" 
          height="450" 
          style={{ border: 0, borderRadius: 'var(--border-radius)' }} 
          allowFullScreen="" 
          loading="lazy" 
          title="Talgo Office Location"
        />
      </Section>
    </ContactContainer>
  );
};

export default Contact; 